import { JsonViewer } from '@/components/JsonViewer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDateTime } from '@/util/dates';
import type { Recall } from '@rock/types';
import type { FC } from 'react';
import { CodeTag, LlmTag } from './recallTags';

/**
 * Every model call made for this recall — which llm, under which version, how long, how much, what went
 * wrong — and each stage's raw structured output, so a reviewer can see who contributed what before the
 * merge and the guard touched it.
 */
export const RecallAiResultsTab: FC<{ recall: Recall }> = ({ recall }) => {
    const calls = [...recall.aiCalls].sort((a, b) => b.at.localeCompare(a.at));
    const totalCost = recall.aiCalls.reduce((sum, call) => sum + call.costUsd, 0);
    return (
        <div className="flex flex-col gap-4">
            <Card>
                <CardHeader>
                    <CardTitle>Calls</CardTitle>
                    <CardDescription>
                        {calls.length} call(s) · ${totalCost.toFixed(4)} total
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>When</TableHead>
                                <TableHead>Stage</TableHead>
                                <TableHead>LLM</TableHead>
                                <TableHead>Version</TableHead>
                                <TableHead className="text-right">Tokens in / out</TableHead>
                                <TableHead className="text-right">Time</TableHead>
                                <TableHead className="text-right">Cost</TableHead>
                                <TableHead>Error</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {calls.map((call, i) => (
                                <TableRow
                                    key={`${call.at}-${call.llm}-${i}`}
                                    className={call.error ? 'bg-red-50' : undefined}
                                >
                                    <TableCell className="text-xs">{formatDateTime(call.at)}</TableCell>
                                    <TableCell>{call.stage}</TableCell>
                                    <TableCell>
                                        <LlmTag llm={call.llm} />
                                    </TableCell>
                                    <TableCell>
                                        <CodeTag>{call.version}</CodeTag>
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {call.inputTokens.toLocaleString()} / {call.outputTokens.toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {(call.ms / 1000).toFixed(1)} s
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        ${call.costUsd.toFixed(4)}
                                    </TableCell>
                                    <TableCell
                                        className="max-w-[320px] truncate text-xs text-destructive"
                                        title={call.error}
                                    >
                                        {call.error ?? ''}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {calls.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} className="py-6 text-center text-muted-foreground">
                                        No model calls yet.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {recall.classification && (
                <Card>
                    <CardHeader>
                        <CardTitle>Classification</CardTitle>
                        <CardDescription className="flex flex-wrap items-center gap-1">
                            Each classifier's answer; the recall carries the union of type ids.
                            <CodeTag>{recall.classification.classifyVersion}</CodeTag>
                            <CodeTag>catalog {recall.classification.catalogVersion}</CodeTag>
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-md bg-muted p-4">
                            <JsonViewer data={recall.classification.byClassifier} />
                        </div>
                    </CardContent>
                </Card>
            )}

            {recall.extractions &&
                Object.entries(recall.extractions).map(([llm, extraction]) => (
                    <Card key={llm}>
                        <CardHeader>
                            <CardTitle>
                                <LlmTag llm={llm} />
                            </CardTitle>
                            <CardDescription>
                                {extraction.modelRules.length} model rule(s), {extraction.serialRules.length} serial
                                rule(s), {extraction.checks.length} check(s), scope {extraction.scope}, confidence{' '}
                                {extraction.confidence.toFixed(2)}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                                <JsonViewer data={extraction} />
                            </div>
                        </CardContent>
                    </Card>
                ))}

            {recall.verification && (
                <Card>
                    <CardHeader>
                        <CardTitle>Verbatim guard</CardTitle>
                        <CardDescription>
                            What the extractors said that the notice does not, and the reverse.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-2 text-sm">
                        <div>
                            <span className="font-medium">Unverified:</span>{' '}
                            {recall.verification.unverified.length ? recall.verification.unverified.join(', ') : 'none'}
                        </div>
                        <div>
                            <span className="font-medium">Harvested, not extracted:</span>{' '}
                            {recall.verification.harvestedNotExtracted.length
                                ? recall.verification.harvestedNotExtracted.join(', ')
                                : 'none'}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};
